import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Users, MapPin, MessageCircle, Calendar, GraduationCap, ArrowRight, X } from 'lucide-react';
import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';
import api from '@/lib/api';
import SEO from '@/components/SEO';
import PlayersNearYouTeaser from '@/components/PlayersNearYouTeaser';
import { useAuth } from '@/contexts/AuthContext';
import { formatEventCost } from '@/lib/formatEventCost';
import { formatEventDate } from '@/lib/formatEventDate';
import { usePromo } from '@/lib/promoConfig';

// Admin QA flag: `?promo_preview=1` bypasses the auth-hide and the
// per-browser dismiss flag so admins can preview what anonymous visitors
// see without juggling incognito windows.
const PREVIEW_KEY = 'promo_preview';
const isPreviewing = () => {
  try { return new URLSearchParams(window.location.search).get(PREVIEW_KEY) === '1'; }
  catch { return false; }
};

const LOGO_URL = "/logo.png";
const FOUR_WOMEN_IMG = "https://static.prod-images.emergentagent.com/jobs/cc4a5178-07a3-4e39-a8a5-4a64cae369f6/images/0bc1edd8fc27457805fc9b0519f65ddd104720a866af4c05f34e7651653b2b06.png";

const fadeUp = { hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } };

function EventsTeaser() {
  const [events, setEvents] = useState([]);
  useEffect(() => {
    api.get('/events').then(({ data }) => setEvents((data.events || []).slice(0, 3))).catch(() => {});
  }, []);
  if (events.length === 0) return null;
  return (
    <section className="py-20 lg:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <span className="text-sm font-bold uppercase tracking-[0.2em] text-[#b456df]">Events</span>
          <h2 className="text-3xl sm:text-4xl font-semibold mt-3 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Upcoming Mahjong Events
          </h2>
          <p className="text-slate-500 mt-3 max-w-xl mx-auto">Join teaching sessions, tournaments, and casual play events hosted by the community.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {events.map(event => (
            <div key={event.event_id} className="p-6 rounded-2xl bg-white border border-slate-100 hover:shadow-md transition-all duration-300">
              <div className="flex items-center gap-2 mb-3">
                <Calendar className="w-4 h-4 text-[#56c6df]" />
                <span className="text-sm text-slate-500">
                  {event.event_date ? formatEventDate(event.event_date, { month: 'short', day: 'numeric' }) : 'TBD'}
                </span>
                {event.is_free
                  ? <span className="ml-auto text-xs font-medium text-[#56dfb4] bg-[#56dfb4]/10 px-2 py-0.5 rounded-full">FREE</span>
                  : <span className="ml-auto text-xs font-medium text-slate-700 bg-slate-100 px-2 py-0.5 rounded-full">{formatEventCost(event)}</span>}
              </div>
              <h3 className="font-semibold mb-1 line-clamp-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{event.title}</h3>
              <p className="text-sm text-slate-500 flex items-center gap-1 mb-2">
                <MapPin className="w-3 h-3" /> {event.venue_name}{event.city && `, ${event.city}`}
              </p>
              <p className="text-xs text-slate-400 line-clamp-2">{event.description}</p>
            </div>
          ))}
        </div>
        <div className="text-center">
          <Link to="/events">
            <Button variant="outline" className="rounded-full px-8 border-2 hover:border-[#b456df] hover:text-[#b456df]">
              View All Events
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
}

export default function HomePage() {
  const { user } = useAuth();
  const purposes = [
    { icon: Calendar, title: 'All Local Events in One Place', desc: 'See every local Mahjong tournament, teaching session, and open play in your area — no more scrolling through social media to track down what\'s happening.', color: '#56c6df', link: '/events', linkText: 'Browse Events' },
    { icon: Users, title: 'Find Local Players', desc: 'Connect with other Mahjong players near you looking for a game, filtered by skill level, availability, and distance.', color: '#56dfb4', link: '/find-players', linkText: 'Find Players' },
    { icon: MapPin, title: 'Public Play Locations', desc: 'Discover local public places — coffee shops, libraries, community centers — where you can meet up for a game.', color: '#f59e0b', link: '/play-locations', linkText: 'See Locations' },
    { icon: GraduationCap, title: 'Instructors Close to You', desc: 'Find verified American Mahjong instructors near you, whether you\'re brand new to the game or looking to sharpen your skills.', color: '#df5681', link: '/instructors', linkText: 'Meet Instructors' },
    { icon: MessageCircle, title: 'Safe Discussion Board', desc: 'Ask questions, share tips, hunt for deals, and talk all things Mahjong in a moderated, welcoming community forum.', color: '#b456df', link: '/forum', linkText: 'Open the Forum' },
  ];

  // Organization JSON-LD — powers Google's Knowledge Panel and enables
  // sitelinks for branded queries like "Meet For Mahjong". The `sameAs`
  // array confirms which social profiles belong to us, which is what
  // wires the Knowledge Panel together.
  // Spec: https://schema.org/Organization
  const orgJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Meet For Mahjong',
    alternateName: 'MeetForMahjong',
    url: 'https://meetformahjong.com',
    logo: LOGO_URL,
    description: 'Meet For Mahjong is a community platform that brings local American Mahjong events, players, instructors, public play locations, and a safe discussion board together in one place.',
    sameAs: [
      'https://www.facebook.com/meetformahjong',
      'https://www.instagram.com/meetformahjong',
    ],
    founder: { '@type': 'Person', name: 'Rachel Rolader' },
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer support',
      url: 'https://meetformahjong.com/contact',
      availableLanguage: ['English'],
    },
  };

  // Homepage promo strip: surfaces the current campaign to anonymous
  // visitors without the scrolling-marquee distraction. Driven by the
  // backend-managed promo config — admins toggle the active promo from
  // `/admin/coupons`. Logged-in users skip the strip entirely (they see
  // promos surfaced contextually on Pricing + Subscription pages).
  const { promo, isPromoActive } = usePromo();
  const [showPromo, setShowPromo] = useState(false);
  useEffect(() => {
    const preview = isPreviewing();
    if (user && !preview) { setShowPromo(false); return; }
    if (!isPromoActive) { setShowPromo(false); return; }
    try {
      if (!preview && window.localStorage.getItem(promo.dismissKey) === '1') { setShowPromo(false); return; }
    } catch { /* localStorage may be unavailable in private mode */ }
    setShowPromo(true);
  }, [user, isPromoActive, promo.dismissKey]);
  const dismissPromo = () => {
    setShowPromo(false);
    if (!isPreviewing()) {
      try { window.localStorage.setItem(promo.dismissKey, '1'); } catch { /* ignore */ }
    }
  };

  return (
    <div data-testid="home-page">
      <SEO
        title="Find Local American Mahjong Players, Events &amp; Instructors"
        description="Find local American Mahjong players, events, instructors, and public play locations. Join our discussion board and connect with Mahjong lovers near you."
        path="/"
        jsonLd={orgJsonLd}
      />

      {showPromo && (
        <section
          data-testid="home-mfmfree3-promo"
          className="border-b border-[#df5681]/20"
          style={{ backgroundImage: 'linear-gradient(135deg, rgba(86,223,180,0.08), rgba(86,198,223,0.08), rgba(180,86,223,0.08))' }}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2.5 flex flex-col sm:flex-row items-center justify-center sm:justify-between gap-2 text-center sm:text-left">
            <p className="text-sm font-medium text-slate-700">
              <span className="inline-block bg-[#df5681] text-white text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full mr-2 align-middle">
                {promo.badge}
              </span>
              First <span className="font-bold">{promo.headline}</span> with code{' '}
              <Link to={promo.ctaPath} className="font-bold underline decoration-2 underline-offset-2 hover:text-[#b456df]" data-testid="home-promo-code-link">{promo.code}</Link>
              {' '}— {promo.bodySuffix}
              <span className="hidden sm:inline text-slate-500"> {promo.endsLabel}</span>
            </p>
            <button
              type="button"
              onClick={dismissPromo}
              className="text-slate-400 hover:text-slate-700 transition-colors p-1 -m-1"
              aria-label="Dismiss promo"
              data-testid="home-promo-dismiss-btn"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </section>
      )}

      {/* Welcome-back banner for already-logged-in members */}
      {user && user.first_name && (
        <section className="bg-gradient-to-r from-[#56dfb4]/10 via-[#56c6df]/10 to-[#b456df]/10 border-b border-[#56dfb4]/20" data-testid="home-welcome-back-banner">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#56dfb4]/30 flex items-center justify-center text-base font-bold text-[#0f172a]">
                {user.first_name[0].toUpperCase()}
              </div>
              <div>
                <p className="font-semibold text-slate-800" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Welcome back, {user.first_name}!
                </p>
                <p className="text-xs text-slate-500" data-testid="home-last-active-line">
                  {(() => {
                    const t = user.last_active_at && new Date(user.last_active_at);
                    if (!t || Number.isNaN(t.getTime())) {
                      return 'Pick up where you left off — your dashboard is one click away.';
                    }
                    const diffMin = Math.max(0, Math.floor((Date.now() - t.getTime()) / 60000));
                    if (diffMin < 5) return 'Active now — your dashboard is one click away.';
                    if (diffMin < 60) return `Last active ${diffMin} min ago — pick up where you left off.`;
                    const diffHr = Math.floor(diffMin / 60);
                    if (diffHr < 24) return `Last active ${diffHr} hour${diffHr === 1 ? '' : 's'} ago — pick up where you left off.`;
                    const diffDay = Math.floor(diffHr / 24);
                    return `Last active ${diffDay} day${diffDay === 1 ? '' : 's'} ago — pick up where you left off.`;
                  })()}
                </p>
              </div>
            </div>
            <Link to="/dashboard">
              <Button className="rounded-full bg-[#0f172a] text-white hover:bg-slate-800 gap-1 text-sm" data-testid="home-welcome-dashboard-btn">
                Go to Dashboard <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
        </section>
      )}

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-[#fafaf9]">
        <div className="absolute inset-0 opacity-5 pointer-events-none">
          <div className="absolute top-20 left-20 w-72 h-72 bg-[#56dfb4] rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-[#df5681] rounded-full blur-3xl" />
          <div className="absolute top-40 right-40 w-64 h-64 bg-[#b456df] rounded-full blur-3xl" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div initial="hidden" animate="visible" variants={fadeUp} transition={{ duration: 0.6 }}>
              <span className="inline-block px-4 py-1.5 bg-[#56dfb4]/10 text-[#0f172a] rounded-full text-sm font-medium mb-6">
                American Mahjong Community
              </span>
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-[#0f172a] mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Find Your Next<br />
                <span className="bg-clip-text text-transparent" style={{ backgroundImage: 'linear-gradient(135deg, #56dfb4, #56c6df, #b456df)' }}>
                  Mahjong Game
                </span>
              </h1>
              <p className="text-lg text-slate-600 mb-8 max-w-lg leading-relaxed">
                The one place for American Mahjong lovers to find local events, discover nearby players and play locations, connect with instructors, and join a safe community of fellow Mahjong fans.
              </p>
              <p className="text-sm text-slate-700 font-medium mb-4" data-testid="hero-login-prompt">
                Already a member?{' '}
                <Link
                  to="/login"
                  className="font-semibold bg-clip-text text-transparent hover:underline"
                  style={{ backgroundImage: 'linear-gradient(135deg, #56dfb4, #56c6df, #b456df)' }}
                  data-testid="hero-login-link"
                >
                  Log in here &rarr;
                </Link>
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/register">
                  <Button
                    className="rounded-full text-white hover:brightness-110 transition-all px-8 py-6 text-base font-semibold border-0"
                    style={{ backgroundImage: 'linear-gradient(135deg, #56dfb4, #56c6df, #b456df)' }}
                    data-testid="hero-cta-btn"
                  >
                    Get Started Free
                  </Button>
                </Link>
                <Link to="/about">
                  <Button
                    variant="outline"
                    className="rounded-full px-8 py-6 text-base font-semibold border-2 border-[#56c6df] bg-white hover:bg-[#56c6df]/5 hover:border-[#b456df] transition-colors"
                    data-testid="hero-learn-more-btn"
                  >
                    <span
                      className="bg-clip-text text-transparent"
                      style={{ backgroundImage: 'linear-gradient(135deg, #56dfb4, #56c6df, #b456df)' }}
                    >
                      Learn More
                    </span>
                  </Button>
                </Link>
              </div>
              <div className="flex flex-wrap gap-3 mt-4">
                <Link to="/events">
                  <Button className="rounded-full bg-[#df5681] text-white hover:bg-[#c44570] px-8 py-5 text-base font-medium gap-2" data-testid="hero-events-btn">
                    <Calendar className="w-5 h-5" /> Mahjong Events Nearby
                  </Button>
                </Link>
                <Link to="/instructors">
                  <Button className="rounded-full bg-[#56dfb4] text-slate-900 hover:bg-[#3ec49b] px-8 py-5 text-base font-medium gap-2" data-testid="hero-instructors-btn">
                    <GraduationCap className="w-5 h-5" /> Instructors
                  </Button>
                </Link>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <div className="relative flex flex-col items-center gap-6">
                {/* Logo (hero right) */}
                <div className="relative w-full flex items-center justify-center py-6">
                  <img
                    src={LOGO_URL}
                    alt="Meet For Mahjong logo"
                    className="w-full max-w-md md:max-w-lg lg:max-w-xl h-auto drop-shadow-2xl"
                  />
                </div>
              </div>
              <div className="absolute -bottom-4 -left-4 bg-white rounded-2xl p-4 shadow-lg border border-slate-100">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#56dfb4]/20 rounded-full flex items-center justify-center">
                    <Users className="w-5 h-5 text-[#0f172a]" />
                  </div>
                  <div>
                    <p className="font-semibold text-sm">1000+ Players</p>
                    <p className="text-xs text-slate-500">Active Community</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Five Purposes (What This Site Is For) */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Players-near-you teaser — silently hides for paid users
              and when the API returns 0 players. */}
          <PlayersNearYouTeaser
            zipCode={user?.zip_code || ''}
            className="mb-16"
          />
          <div className="text-center mb-16">
            <span className="text-sm font-bold uppercase tracking-[0.2em] text-[#df5681]">What You Can Do Here</span>
            <h2 className="text-3xl sm:text-4xl font-semibold mt-3 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Everything Mahjong, all in one place.
            </h2>
            <p className="text-slate-500 mt-4 max-w-2xl mx-auto">
              Come for the Mahjong. Stay for the people.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {purposes.map((p, i) => (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="p-6 rounded-2xl bg-white border border-slate-100 hover:shadow-md hover:-translate-y-1 transition-all duration-300"
                data-testid={`home-purpose-${i + 1}`}
              >
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: `${p.color}15` }}>
                  <p.icon className="w-6 h-6" style={{ color: p.color }} />
                </div>
                <h3 className="font-semibold text-lg mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>{p.title}</h3>
                <p className="text-slate-500 text-sm leading-relaxed mb-4">{p.desc}</p>
                <Link to={p.link} className="text-sm font-semibold" style={{ color: p.color }}>
                  {p.linkText} →
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Community Section */}
      <section className="py-20 lg:py-28 bg-[#fafaf9]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="rounded-3xl overflow-hidden shadow-xl">
              <img src={FOUR_WOMEN_IMG} alt="Diverse women playing American Mahjong" className="w-full h-[400px] object-cover" />
            </div>
            <div>
              <span className="text-sm font-bold uppercase tracking-[0.2em] text-[#56c6df]">Community</span>
              <h2 className="text-3xl sm:text-4xl font-semibold mt-3 tracking-tight mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Join a Growing Community of Players
              </h2>
              <p className="text-slate-600 mb-4 leading-relaxed">
                American Mahjong is more than a game — it's a social experience. Meet For Mahjong helps you find players who share your passion, fit your schedule, and live nearby.
              </p>
              <p className="text-slate-600 mb-8 leading-relaxed">
                Whether you prefer public meetups at coffee shops or private games at home, we'll help you find the perfect group.
              </p>
              <Link to="/register">
                <Button className="rounded-full bg-[#df5681] text-white hover:bg-[#c44570] px-8 py-3 font-medium" data-testid="community-cta-btn">
                  Join the Community
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Events Teaser */}
      <EventsTeaser />

      {/* CTA */}
      <section className="py-20 bg-[#0f172a]">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Ready to Find Your Mahjong Game?
          </h2>
          <p className="text-slate-400 mb-8 text-lg">
            Sign up free and start connecting with players in your area today.
          </p>
          <Link to="/register">
            <Button className="rounded-full bg-[#56dfb4] text-slate-900 hover:bg-[#3ec49b] px-10 py-6 text-lg font-medium">
              Get Started — It's Free
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
